import { Flex, StackProps, VStack } from '@chakra-ui/react';
import { FC, ReactElement, ReactNode, useReducer } from 'react';
import useSWRImmutable from 'swr/immutable';

import {
  ChatDataProvider,
  ChatStateProvider,
  ChatUpdateProvider,
} from '@/components/ui/shared/conversation/Chat/Chat.context';
import { initialReducerState, reducer } from '@/components/ui/shared/conversation/Chat/Chat.reducer';
import { ResourceType } from '@/enums';
import { getToken } from '@/hooks/liveMessages/helper';
import { useBeforeUnload } from '@/hooks/useBeforeUnload';
import { useConversation } from '@/hooks/useConversation';
import { useOnUnmount } from '@/hooks/useOnUnmount';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useTwilioConnection } from '@/hooks/useTwilioConnection';
import { Token } from '@/resources/Token';

interface IChatProps extends StackProps {
  conversationSid: string;
  resourceType: ResourceType;
  resourceId: string;
  errorSlot?: ReactElement;
  loaderSlot?: ReactElement;
  unauthorisedSlot?: ReactElement;
  children: ReactNode;
}

export const Chat: FC<IChatProps> = ({
  conversationSid,
  resourceId,
  resourceType,
  loaderSlot,
  unauthorisedSlot,
  children,
  ...rest
}) => {
  const { store } = useStores();
  const { user, sessionError } = useSession();

  const [state, dispatch] = useReducer(reducer, initialReducerState);

  // get token
  const { data: tokenResponse, mutate: mutateToken, error: tokenError } = useSWRImmutable(
    user?.id ? 'tokens' : null,
    () => getToken(store, user?.id, resourceId, resourceType)
  );

  const token = tokenResponse?.data as Token;

  // get client
  const { client, error: connectionError } = useTwilioConnection(token?.value, {
    onTokenAboutToExpire: () => mutateToken(),
  });

  // get conversation?
  const { data: conversation, leave, error: conversationError } = useConversation(client, conversationSid);

  useOnUnmount(leave);
  useBeforeUnload(leave);

  if (!user && sessionError) {
    return (
      <Flex h="full" alignItems="stretch" justifyContent="center" {...rest}>
        {unauthorisedSlot}
      </Flex>
    );
  }

  if (tokenError || conversationError || connectionError) {
    throw tokenError || conversationError || connectionError;
  }

  if (!client || !conversation) {
    return (
      <Flex h="full" alignItems="stretch" justifyContent="center" {...rest}>
        {loaderSlot}
      </Flex>
    );
  }

  return (
    <ChatDataProvider value={{ client, conversation }}>
      <ChatUpdateProvider value={dispatch}>
        <ChatStateProvider value={state}>
          <VStack spacing={0} h="full" align="stretch" {...rest}>
            {children}
          </VStack>
        </ChatStateProvider>
      </ChatUpdateProvider>
    </ChatDataProvider>
  );
};
